/**
 * A `refusal` event, drawn from Figma's `Card/Sem leitura` (`LrdQopN1jNWKJFWxOkn0IY`
 * node `360:14552`): the dashed `EmptyState` shell every absence on this
 * screen shares, never a zero and never an apology — the title names the
 * refusal itself, and the description names what would have been read
 * instead of just saying nothing came back.
 */

import { Ban, Eye, HelpCircle, type LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';

import { EmptyState } from '../../../chrome/state';
import { at, isAssistantMessageKey, type AssistantMessageKey } from './messages';
import type { RefusalData } from './schemas';

const REFUSAL_ICON: Record<RefusalData['reason'], LucideIcon> = {
  no_reading: Eye,
  tool_limit: Ban,
  unknown_tool: HelpCircle,
};

const REFUSAL_TITLE_KEY: Record<RefusalData['reason'], AssistantMessageKey> = {
  no_reading: 'refusal.no_reading.title',
  tool_limit: 'refusal.tool_limit.title',
  unknown_tool: 'refusal.unknown_tool.title',
};

/** A tool's reader-facing name; its internal name never reaches the screen. */
function toolLabel(name: string): string {
  const key = `tool.${name}`;
  return at(isAssistantMessageKey(key) ? key : 'tool.fallback');
}

export function Refusal(props: { refusal: RefusalData }): ReactNode {
  const { refusal } = props;
  return (
    <EmptyState
      icon={REFUSAL_ICON[refusal.reason]}
      title={at(REFUSAL_TITLE_KEY[refusal.reason])}
      description={
        refusal.would_have_read.length === 0
          ? undefined
          : at('refusal.would_have_read', {
              items: refusal.would_have_read.map(toolLabel).join(', '),
            })
      }
    />
  );
}
